import { getUtms } from './utm';
import { getHomeSlugForLocale } from './localizedSlugs';
import {
  ENGLISH_LOCALE,
  LOCALE_CANDIDATES,
  SPANISH_VARIANTS,
  isEnglishLocale,
  isSpanishLocale,
  normalizeLocale,
} from './locale';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL;
const STRAPI_API_URL = import.meta.env.VITE_STRAPI_API_URL;
const DEFAULT_POPULATE = '*';
const STRAPI_CACHE_TTL_MS = 30_000;
const AVAILABLE_LOCALES_CACHE_TTL_MS = 30_000;
const LEAD_SOURCE_VALUES = new Set(['instagram', 'facebook', 'web', 'referido', 'phone', 'email', 'otro']);
const strapiResponseCache = new Map();
const strapiInFlightRequests = new Map();
const availableLocalesCache = new Map();
const availableLocalesInFlightRequests = new Map();

const getTimedCachedValue = (cacheMap, key) => {
  const cachedEntry = cacheMap.get(key);
  if (!cachedEntry) return null;

  if (cachedEntry.expiresAt <= Date.now()) {
    cacheMap.delete(key);
    return null;
  }

  return cachedEntry.data;
};

const setTimedCachedValue = (cacheMap, key, data, ttlMs) => {
  cacheMap.set(key, {
    data,
    expiresAt: Date.now() + ttlMs,
  });
};

const getCachedStrapiResponse = (url) => getTimedCachedValue(strapiResponseCache, url);

const setCachedStrapiResponse = (url, data) => {
  setTimedCachedValue(strapiResponseCache, url, data, STRAPI_CACHE_TTL_MS);
};

const normalizeMediaText = (value) => (
  typeof value === 'string' ? value.trim() : ''
);

const normalizePreferredFormats = (preferredFormats) => {
  if (Array.isArray(preferredFormats)) {
    return preferredFormats.filter(Boolean);
  }

  return preferredFormats ? [preferredFormats] : [];
};

const getMediaFormats = (media) => (
  media?.formats
  ?? media?.data?.formats
  ?? media?.data?.attributes?.formats
  ?? {}
);

const getMediaField = (media, field) => {
  if (!media || typeof media === 'string') return '';

  return normalizeMediaText(
    media[field]
    ?? media?.data?.[field]
    ?? media?.data?.attributes?.[field]
  );
};

// Versión numérica de getMediaField: lee width/height del objeto de media
// en cualquiera de sus formas (plana, .data, .data.attributes). Devuelve
// un Number finito o undefined. Se usa para que las <img> lleven atributos
// width/height intrínsecos y el navegador reserve el box (reduce CLS).
const getMediaNumber = (media, field) => {
  if (!media || typeof media === 'string') return undefined;

  const raw = media[field] ?? media?.data?.[field] ?? media?.data?.attributes?.[field];
  const num = Number(raw);
  return Number.isFinite(num) ? num : undefined;
};

const normalizeLeadSource = (value) => {
  const normalized = String(value || '').trim().toLowerCase();

  if (!normalized) return 'web';
  if (LEAD_SOURCE_VALUES.has(normalized)) return normalized;
  if (normalized === 'website' || normalized === 'sitio web' || normalized.startsWith('/')) return 'web';
  if (normalized.includes('instagram')) return 'instagram';
  if (normalized.includes('facebook') || normalized.includes('meta')) return 'facebook';
  if (normalized.includes('refer')) return 'referido';
  if (normalized.includes('phone') || normalized.includes('telefono') || normalized.includes('tel')) return 'phone';
  if (normalized.includes('mail') || normalized.includes('correo') || normalized.includes('email')) return 'email';

  return 'web';
};

const getDynamicPagePopulateParams = () => ({
  'populate[seo]': 'true',
  'populate[localizations][fields][0]': 'slug',
  'populate[localizations][fields][1]': 'locale',
  'populate[seo][populate][metaImage][fields][0]': 'url',
  'populate[seo][populate][metaImage][fields][1]': 'alternativeText',
  'populate[seo][populate][metaImage][fields][2]': 'caption',
  'populate[seo][populate][metaImage][fields][3]': 'width',
  'populate[seo][populate][metaImage][fields][4]': 'height',
  'populate[seo][populate][metaImage][fields][5]': 'mime',
  'populate[hero][populate]': '*',
  'populate[items][populate]': '*',
  'populate[sections][populate]': '*',
  'populate[sections][on][blocks.scroll-cta][populate]': '*',
  'populate[sections][on][blocks.espacio-vacio][populate]': '*',
  'populate[sections][on][blocks.contact-form][populate][backgroundImage]': 'true',
  'populate[sections][on][blocks.contact-form][populate][fields][populate]': '*',
  'populate[sections][on][blocks.contact-form][populate][content][populate]': '*',
  'populate[sections][on][blocks.contact-form][populate][thankYou][populate]': '*',
  'populate[sections][on][blocks.cotizador][populate][imagen]': 'true',
  'populate[sections][on][blocks.cotizador][populate][campos][populate]': '*',
  'populate[sections][on][blocks.cotizador][populate][esquemasDePago][populate]': '*',
  'populate[sections][on][blocks.cotizador][populate][extras][populate]': '*',
  'populate[sections][on][blocks.description][populate][icon]': 'true',
  'populate[sections][on][blocks.description][populate][content][populate]': '*',
  'populate[sections][on][blocks.descripcion-home][populate][backgroundImage]': 'true',
  'populate[sections][on][blocks.descripcion-home][populate][icon]': 'true',
  'populate[sections][on][blocks.descripcion-home][populate][content][populate]': '*',
  'populate[sections][on][blocks.descripcion-modelo][populate][icon]': 'true',
  'populate[sections][on][blocks.descripcion-modelo][populate][content][populate]': '*',
  'populate[sections][on][blocks.floorplan][populate][backgroundImage]': 'true',
  'populate[sections][on][blocks.floorplan][populate][galeria][populate]': '*',
  'populate[sections][on][blocks.gallery][populate][items][populate]': '*',
  'populate[sections][on][blocks.gallery-info][populate][items][populate][image]': 'true',
  'populate[sections][on][blocks.gallery-info][populate][items][populate][content][populate]': '*',
  'populate[sections][on][blocks.gallery-info][populate][items][populate][button][populate][icon]': 'true',
  'populate[sections][on][blocks.hero-gallery][populate]': '*',
  'populate[sections][on][blocks.hero-iframe360][populate]': '*',
  'populate[sections][on][blocks.hero-image][populate]': '*',
  'populate[sections][on][blocks.hero-secciones][populate]': '*',
  'populate[sections][on][blocks.hero-video][populate]': '*',
  'populate[sections][on][blocks.popup-fullscreen][populate][media]': 'true',
  'populate[sections][on][blocks.popup-fullscreen][populate][posterImage]': 'true',
  'populate[sections][on][blocks.legal-text][populate]': '*',
  'populate[sections][on][blocks.location][populate]': '*',
  'populate[sections][on][blocks.main-features][populate][features][populate]': '*',
  'populate[sections][on][blocks.model-header][populate]': '*',
  'populate[sections][on][blocks.secondary-features][populate][features][populate]': '*',
  'populate[sections][on][blocks.availability-list][populate]': '*',
  'populate[sections][on][blocks.tipologias][populate][cards][populate]': '*',
  'populate[sections][on][blocks.faq][populate][items][populate]': '*',
});

export const getStrapiMedia = (media) => {
  if (!media) return null;

  const url = typeof media === 'string'
    ? media
    : media?.url || media?.data?.url || media?.data?.attributes?.url;

  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${STRAPI_URL}${url}`;
};

export const getStrapiMediaAttributes = (media, options = {}) => {
  const {
    altFallback = '',
    titleFallback = '',
    decorative = false,
    preferredFormats = [],
  } = options;
  const formatCandidates = normalizePreferredFormats(preferredFormats);
  const formats = getMediaFormats(media);
  const preferredSource = formatCandidates
    .map((formatName) => formats?.[formatName])
    .find((variant) => variant?.url);
  const src = getStrapiMedia(preferredSource || media);

  if (!src) {
    return {
      src: null,
      alt: decorative ? '' : normalizeMediaText(altFallback),
      title: decorative ? undefined : normalizeMediaText(titleFallback) || undefined,
    };
  }

  if (decorative) {
    return { src, alt: '', title: undefined };
  }

  const alt = getMediaField(media, 'alternativeText') || normalizeMediaText(altFallback);
  const title = getMediaField(media, 'caption') || normalizeMediaText(titleFallback) || undefined;

  // Dimensiones intrínsecas de la imagen servida. Priorizamos el formato
  // realmente seleccionado (preferredSource, ej. large/medium) y caemos al
  // top-level del media. Se exponen como números para que las <img> puedan
  // usarlos como atributos width/height y el navegador reserve el box
  // antes de descargar la imagen (reduce CLS).
  const width = (preferredSource && getMediaNumber(preferredSource, 'width'))
    || getMediaNumber(media, 'width');
  const height = (preferredSource && getMediaNumber(preferredSource, 'height'))
    || getMediaNumber(media, 'height');

  return { src, alt, title, width, height };
};

// Devuelve un string CSS de aspect-ratio ("1600 / 900") a partir de las
// dimensiones intrínsecas de un objeto retornado por getStrapiMediaAttributes,
// o null si no hay suficientes datos. Úsalo para inyectar
// style={{ '--image-aspect-ratio': getMediaAspectRatio(media) }} en <img>
// cuyo CSS tiene aspect-ratio: var(--image-aspect-ratio, <fallback>).
export const getMediaAspectRatio = (media) => {
  const width = media?.width;
  const height = media?.height;
  if (!Number.isFinite(Number(width)) || !Number.isFinite(Number(height))) return null;
  if (Number(width) <= 0 || Number(height) <= 0) return null;
  return `${width} / ${height}`;
};

export const fetchStrapi = async (endpoint, params = {}, options = {}) => {
  const { skipPopulate = false, suppressErrors = false } = options;
  const normalizedParams = { ...params };

  if (!skipPopulate && normalizedParams.populate == null) {
    normalizedParams.populate = DEFAULT_POPULATE;
  }

  const cleanParams = Object.fromEntries(
    Object.entries(normalizedParams).filter(([, v]) => v != null && v !== '')
  );
  const queryString = new URLSearchParams(cleanParams).toString();
  const url = `${STRAPI_API_URL}${endpoint}${queryString ? `?${queryString}` : ''}`;

  const cachedResponse = getCachedStrapiResponse(url);
  if (cachedResponse) {
    return cachedResponse;
  }

  const inFlightRequest = strapiInFlightRequests.get(url);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const request = (async () => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        // Suppress noisy console errors for 404s — these are expected for
        // section aliases (e.g. /amenidades) that don't have a real page.
        if (!suppressErrors && response.status !== 404) {
          const errorText = await response.text();
          console.error('Strapi error response:', errorText);
        }
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setCachedStrapiResponse(url, data);
      return data;
    } catch (error) {
      if (!suppressErrors && !error?.message?.includes('404')) {
        console.error('Error fetching Strapi:', error);
      }
      throw error;
    } finally {
      strapiInFlightRequests.delete(url);
    }
  })();

  strapiInFlightRequests.set(url, request);
  return request;
};

export const getGlobalData = async (locale) => {
  const fetchSafe = async (endpoint, params = {}, options = {}) => {
    try {
      return await fetchStrapi(endpoint, params, options);
    } catch (error) {
      console.warn(`Failed to fetch ${endpoint}:`, error.message);
      return { data: null };
    }
  };

  const baseParams = {};
  if (locale) {
    baseParams.locale = normalizeLocale(locale);
  }

  const headerParams = {
    ...baseParams,
    'populate[navigation][populate][children]': '*',
    'populate[ctaButton]': '*',
    'populate[contactMenu][populate][items]': '*',
    'populate[logo][fields][0]': 'url',
    'populate[logo][fields][1]': 'alternativeText',
    'populate[logo][fields][2]': 'width',
    'populate[logo][fields][3]': 'height',
    'populate[logo][fields][4]': 'caption',
  };

  const footerParams = {
    ...baseParams,
    'populate[logo]': 'true',
    'populate[creatorLogo]': 'true',
    'populate[navigation]': 'true',
    'populate[legalLinks]': 'true',
  };

  const [companyInfo, header, footer, themeConfig] = await Promise.all([
    fetchSafe('/company-info', baseParams, { skipPopulate: true }),
    fetchSafe('/header', headerParams, { skipPopulate: true }),
    fetchSafe('/footer', footerParams, { skipPopulate: true }),
    fetchSafe('/theme-config', baseParams),
  ]);

  return {
    companyInfo: companyInfo?.data,
    header: header?.data,
    footer: footer?.data,
    themeConfig: themeConfig?.data,
  };
};

// Un documento en draft puede llegar con data desde el endpoint público;
// solo cuenta como disponible si tiene publishedAt.
const isPublishedDocument = (entry) => Boolean(entry && entry.publishedAt != null);

const hasPublishedCompanyInfo = async (locale) => {
  if (!locale) return false;

  try {
    const response = await fetchStrapi('/company-info', { locale }, { skipPopulate: true });
    const entry = response?.data;
    if (!isPublishedDocument(entry)) return false;
    // Si Strapi responde con la entrada de otro locale (fallback de singleton),
    // el locale pedido no tiene company-info propio.
    if (entry.locale && entry.locale !== locale) return false;
    return true;
  } catch {
    return false;
  }
};

// El home del locale (home / homeeng) también debe estar publicado: si queda
// en draft, entrar a ese idioma resuelve en 404.
const hasPublishedHomePage = async (locale) => {
  if (!locale) return false;

  try {
    const response = await fetchStrapi(
      '/dynamic-pages',
      { locale, 'filters[slug][$eq]': getHomeSlugForLocale(locale) },
      { skipPopulate: true },
    );
    const entries = Array.isArray(response?.data) ? response.data : [];
    return entries.some(
      (entry) => isPublishedDocument(entry) && (!entry.locale || entry.locale === locale)
    );
  } catch {
    return false;
  }
};

// Un idioma se ofrece en el switcher solo si SU company-info y SU home están
// publicados (no draft) en ese locale.
const isLocaleContentPublished = async (locale) => {
  const [companyInfoPublished, homePublished] = await Promise.all([
    hasPublishedCompanyInfo(locale),
    hasPublishedHomePage(locale),
  ]);
  return companyInfoPublished && homePublished;
};

const getFirstAvailableLocale = async (candidates) => {
  for (const locale of candidates) {
    if (await isLocaleContentPublished(locale)) {
      return locale;
    }
  }

  return null;
};

export const getAvailableLocales = async (currentLocale) => {
  const normalizedCurrentLocale = normalizeLocale(currentLocale);
  const cacheKey = normalizedCurrentLocale || '__default__';
  const cachedLocales = getTimedCachedValue(availableLocalesCache, cacheKey);

  if (cachedLocales) {
    return cachedLocales;
  }

  const inFlightRequest = availableLocalesInFlightRequests.get(cacheKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const request = (async () => {
    let availableLocales = [];

    if (isSpanishLocale(normalizedCurrentLocale)) {
      const spanishLocale = await getFirstAvailableLocale([
        normalizedCurrentLocale,
        ...SPANISH_VARIANTS.filter((locale) => locale !== normalizedCurrentLocale),
      ]);
      const englishAvailable = await isLocaleContentPublished(ENGLISH_LOCALE);

      availableLocales = [spanishLocale, englishAvailable ? ENGLISH_LOCALE : null].filter(Boolean);
    } else if (isEnglishLocale(normalizedCurrentLocale)) {
      const englishAvailable = await isLocaleContentPublished(ENGLISH_LOCALE);
      const spanishLocale = await getFirstAvailableLocale(SPANISH_VARIANTS);

      availableLocales = [englishAvailable ? ENGLISH_LOCALE : null, spanishLocale].filter(Boolean);
    }

    if (availableLocales.length === 0) {
      const checks = await Promise.all(
        LOCALE_CANDIDATES.map(async (locale) => (
          await isLocaleContentPublished(locale) ? locale : null
        ))
      );

      availableLocales = checks.filter(Boolean);
    }

    const uniqueLocales = Array.from(new Set(availableLocales));
    setTimedCachedValue(
      availableLocalesCache,
      cacheKey,
      uniqueLocales,
      AVAILABLE_LOCALES_CACHE_TTL_MS,
    );

    return uniqueLocales;
  })();

  availableLocalesInFlightRequests.set(cacheKey, request);

  try {
    return await request;
  } finally {
    availableLocalesInFlightRequests.delete(cacheKey);
  }
};

export const getPriceMap = async (locale) => {
  const params = {};
  if (locale) params.locale = normalizeLocale(locale);

  try {
    const response = await fetchStrapi('/dynamic-pages/price-map', params, { skipPopulate: true });
    return response?.data || {};
  } catch (error) {
    console.warn('Failed to fetch price map:', error.message);
    return {};
  }
};

export const getLegalRouteMap = async (locale) => {
  const params = {};
  if (locale) params.locale = normalizeLocale(locale);

  try {
    const response = await fetchStrapi('/dynamic-pages/legal-link-map', params, { skipPopulate: true });
    const routeMap = response?.data || {};

    return Object.fromEntries(
      Object.entries(routeMap).map(([path, entry]) => [
        path,
        {
          ...entry,
          fileUrl: entry?.fileUrl ? getStrapiMedia(entry.fileUrl) : null,
        },
      ])
    );
  } catch (error) {
    console.warn('Failed to fetch legal route map:', error.message);
    return {};
  }
};

export const getPageBySlug = async (slug, locale) => {
  const params = {};
  if (locale) {
    params.locale = normalizeLocale(locale);
  }

  const basePageParams = {
    ...params,
    'filters[slug][$eq]': slug,
  };

  // Claves de populate de bloques que pueden NO existir aún en Strapi (p. ej.
  // blocks.faq antes de modelarlo). Strapi v5 rechaza con 400 cualquier query
  // cuyo `on.blocks.X` referencie un componente inexistente, lo que rompería
  // TODAS las páginas. Aquí las aislamos para poder reintentar sin ellas.
  const OPTIONAL_BLOCK_POPULATE_KEYS = [
    'populate[sections][on][blocks.faq][populate][items][populate]',
  ];

  try {
    const response = await fetchStrapi('/dynamic-pages', {
      ...basePageParams,
      ...getDynamicPagePopulateParams(),
    }, {
      skipPopulate: true,
      suppressErrors: true,
    });
    return response?.data?.[0] || null;
  } catch (error) {
    // 404 solo significa que el slug no es página real (deja decidir al caller).
    if (error?.message?.includes('404')) return null;

    // 400 suele indicar un `on.blocks.X` inválido (bloque no creado en Strapi).
    // Reintentamos sin los populates opcionales para no romper el sitio durante
    // la transición (hasta que el bloque exista en el CMS).
    if (error?.message?.includes('400')) {
      const fullPopulate = getDynamicPagePopulateParams();
      const safePopulate = Object.fromEntries(
        Object.entries(fullPopulate).filter(
          ([key]) => !OPTIONAL_BLOCK_POPULATE_KEYS.includes(key)
        )
      );

      const response = await fetchStrapi('/dynamic-pages', {
        ...basePageParams,
        ...safePopulate,
      }, {
        skipPopulate: true,
        suppressErrors: true,
      });
      return response?.data?.[0] || null;
    }

    throw error;
  }
};

export const getAllPages = async (locale) => {
  const params = {
    'fields[0]': 'slug',
    'fields[1]': 'titulo',
  };

  if (locale) {
    params.locale = normalizeLocale(locale);
  }

  const response = await fetchStrapi('/dynamic-pages', params, { skipPopulate: true });

  return response?.data || [];
};

export const getPopupFullscreenEntries = async (locale) => {
  const params = {
    'fields[0]': 'slug',
    'fields[1]': 'titulo',
    'fields[2]': 'documentId',
    'pagination[pageSize]': 100,
    'populate[sections][on][blocks.popup-fullscreen][populate][media]': 'true',
    'populate[sections][on][blocks.popup-fullscreen][populate][posterImage]': 'true',
  };

  if (locale) {
    params.locale = normalizeLocale(locale);
  }

  try {
    const response = await fetchStrapi('/dynamic-pages', params, { skipPopulate: true });
    const pages = response?.data || [];

    return pages.flatMap((page) => (page.sections || [])
      .filter((section) => section?.__component === 'blocks.popup-fullscreen')
      .map((section) => ({
        ...section,
        pageSlug: page.slug,
        pageTitle: page.titulo,
        pageDocumentId: page.documentId,
      })));
  } catch (error) {
    console.warn('Failed to fetch popup fullscreen entries:', error.message);
    return [];
  }
};

export const submitLead = async (formData) => {
  const url = `${STRAPI_API_URL}/leads/submit`;

  const utms = getUtms(typeof window !== 'undefined' ? window.location.search : '');
  const {
    fullName,
    fullname,
    name,
    email,
    phone,
    country,
    estimatedValue,
    source,
    leadType,
    data,
    campaign,
    promocion,
    IdProducto,
    projectId,
  } = formData || {};

  const resolvedCampaign = campaign || utms.utm_campaign || undefined;

  const nestedData = {
    ...(data || {}),
    ...(leadType ? { leadType } : {}),
    ...(source ? { originalSource: source } : {}),
    ...(Object.keys(utms).length ? { utms } : {}),
  };

  const payload = {
    data: {
      name: name || fullName || fullname || '',
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
      ...(country ? { country } : {}),
      ...(estimatedValue != null ? { estimatedValue } : {}),
      source: normalizeLeadSource(source),
      ...(resolvedCampaign ? { campaign: resolvedCampaign } : {}),
      ...(promocion ? { promocion } : {}),
      ...(IdProducto ? { IdProducto } : {}),
      ...(projectId ? { projectId } : {}),
      ...(typeof window !== 'undefined' && window.location.href ? { url: window.location.href } : {}),
      ...(Object.keys(nestedData).length ? { data: nestedData } : {}),
    },
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error submitting lead:', errorText);
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error submitting lead:', error);
    throw error;
  }
};

export const submitCotizacion = async (cotizacionData) => {
  const url = `${STRAPI_API_URL}/cotizaciones`;
  const utms = getUtms(typeof window !== 'undefined' ? window.location.search : '');

  const payload = {
    ...cotizacionData,
    campaign: cotizacionData?.campaign || utms.utm_campaign || undefined,
    info: {
      ...(cotizacionData?.info || {}),
      utms: Object.keys(utms).length ? utms : undefined,
    },
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: payload }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error submitting cotizacion:', errorText);
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error submitting cotizacion:', error);
    throw error;
  }
};

// ─── FAQ admin (panel interno /faq-admin) ───────────────────────────────────
// Dispara la regeneración de FAQs en Strapi (job /faq-configs/run-now que lee
// del chatbot Flowise, genera con OpenAI y traduce). El endpoint es auth:false
// por diseño del backend; la protección real debe configurarse en Strapi.
// `signal` permite cancelar/timeout desde el caller (AbortController).
export const triggerFaqRegeneration = async ({ signal } = {}) => {
  const url = `${STRAPI_API_URL}/faq-configs/run-now`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      ...(signal ? { signal } : {}),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`Error ${response.status}: ${response.statusText} ${errorText}`.trim());
    }

    return await response.json();
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('La solicitud tardó demasiado y se canceló. Intenta nuevamente.');
    }
    console.error('Error triggering FAQ regeneration:', error);
    throw error;
  }
};

// Lee el singleton faq-config del backend para mostrar info del último run
// (lastRunAt, lastRunResult, enabled, regenerationDays, maxItems, lookbackDays).
// Usa fetch directo (no fetchStrapi) para silenciar 403/404 en consola: es
// esperado que el permiso público `find` no esté habilitado en Strapi, y en
// ese caso la sección "última regeneración" del panel simplemente no se llena.
// Devuelve null si falla — no rompe el panel.
export const getFaqConfig = async () => {
  try {
    const response = await fetch(`${STRAPI_API_URL}/faq-config`);
    if (!response.ok) return null;
    const payload = await response.json();
    return payload?.data || null;
  } catch {
    return null;
  }
};

export default {
  fetchStrapi,
  getStrapiMedia,
  getGlobalData,
  getPageBySlug,
  getAllPages,
  getPopupFullscreenEntries,
  getLegalRouteMap,
  submitLead,
  submitCotizacion,
  triggerFaqRegeneration,
  getFaqConfig,
};
